import type { ReactNode } from 'react';
import { Box, InlineStack, Link, List, ListItem, Text, Tooltip } from '@customerio/pluma-components/react';
import { formatDateTime } from '@customerio/pluma-components/react/utils/formatted-date-time';
import type { Delivery, DeliveryDetail } from '@/services/db/delivery/entity';
import { generatePath } from '@/services/router/generate-path';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import { metaValue } from '../utils/delivery-row-state';
import { formatDurationLater } from '../utils/duration';
import { formatLongTimestamp } from '@/utils/calendar-timestamps';
import {
  deliveryReason,
  deliveryTimestampRows,
  postDeliveryActionRows,
  type TimestampRow,
} from '../utils/delivery-timestamps';

type EntryProps = { label: string; testId: string; children: ReactNode };

/**
 * An absolute-time row: Ember stacks the value under its label (the label is a block
 * `<PlumaText>`), unlike the duration rows which read inline as one sentence.
 */
function Entry({ label, testId, children }: EntryProps) {
  return (
    <Box>
      <Text as="div" color="subtle" size="sm" data-test-delivery-row-expansion-timestamp-name={testId}>
        {label}
      </Text>
      {children}
    </Box>
  );
}

type DeliveryTimestampStackProps = {
  delivery: Delivery;
  detail: DeliveryDetail | undefined;
  isRedeliverableAttempted: boolean;
};

/**
 * The middle column of the expanded row: the creation time, each lifecycle
 * timestamp (as a duration from creation), an optional retry schedule, then
 * either the failure reason or the engagement timestamps, and finally a link
 * to the original delivery for resends.
 *
 * Deferred: the "Scheduled to send" line for queued transactional messages —
 * it gates on the detail's `ready_to_send`, which the data layer's
 * `DeliveryDetail` does not expose.
 */
export function DeliveryTimestampStack({ delivery, detail, isRedeliverableAttempted }: DeliveryTimestampStackProps) {
  const created = delivery.created ?? 0;
  const retryAt = Number(metaValue(delivery, 'message_limit_retry') ?? 0);

  return (
    <>
      {created > 0 ? (
        <Entry label="Created" testId="created">
          <AbsoluteTime timestamp={created} />
        </Entry>
      ) : null}

      {deliveryTimestampRows(delivery).map((row) => (
        <DurationEntry key={row.key} row={row} referenceTimestamp={created} />
      ))}

      {isRedeliverableAttempted && retryAt > 0 ? (
        <Entry label="Scheduled to retry" testId="message_limit_retry">
          <AbsoluteTime timestamp={retryAt} />
        </Entry>
      ) : null}

      <DeliveryReasonRegion delivery={delivery} detail={detail} />

      <OriginalDeliveryLink delivery={delivery} />
    </>
  );
}

type AbsoluteTimeProps = { timestamp: number };

/** An absolute event time in Ember's `LongTimestamp` format ("Today at 11:47:49 am (PDT)"). */
function AbsoluteTime({ timestamp }: AbsoluteTimeProps) {
  return <Text as="div">{formatLongTimestamp(timestamp)}</Text>;
}

type DurationEntryProps = { row: TimestampRow; referenceTimestamp: number };

// Lifecycle durations render inline as a gap from creation ("Sent a few seconds
// later"), not a wall-clock "… ago" — the absolute event time is in the
// tooltip. The absolute-time entries stack the value under the label instead.
function DurationEntry({ row, referenceTimestamp }: DurationEntryProps) {
  return (
    <Box>
      <Text color="subtle" size="sm" data-test-delivery-row-expansion-timestamp-name={row.key}>
        {row.label}
      </Text>{' '}
      <Tooltip content={formatDateTime(row.timestamp * 1000, 'full_with_time')}>
        <Text as="span">{formatDurationLater(referenceTimestamp, row.timestamp)}</Text>
      </Tooltip>
    </Box>
  );
}

type DeliveryReasonRegionProps = { delivery: Delivery; detail: DeliveryDetail | undefined };

function DeliveryReasonRegion({ delivery, detail }: DeliveryReasonRegionProps) {
  const reason = deliveryReason(delivery, detail);

  if (reason.kind === 'errors') {
    return (
      <InlineStack alignInline="space-between">
        <Text color="subtle">Reason</Text>
        <Box flex={1} ml="050" className="fs-mask" data-test-delivery-row-expansion-errors>
          {reason.messages.length === 1 ? (
            <Text color="critical">{reason.messages[0]}</Text>
          ) : (
            <List data-test-delivery-row-expansion-errors-list>
              {reason.messages.map((message) => (
                <ListItem key={message}>
                  <Text color="critical">{message}</Text>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </InlineStack>
    );
  }

  if (reason.kind === 'reason') {
    return (
      <InlineStack alignInline="space-between">
        <Text color="subtle">Reason</Text>
        <Box flex={1} ml="050" className="fs-mask" data-test-delivery-row-expansion-failure-reason>
          <Text color="information">{reason.message}</Text>
        </Box>
      </InlineStack>
    );
  }

  return (
    <>
      {postDeliveryActionRows(delivery).map((row) => (
        <Entry key={row.key} label={row.label} testId={row.key}>
          <AbsoluteTime timestamp={row.timestamp} />
        </Entry>
      ))}
    </>
  );
}

type OriginalDeliveryLinkProps = { delivery: Delivery };

function OriginalDeliveryLink({ delivery }: OriginalDeliveryLinkProps) {
  const originalId = metaValue(delivery, 'orig_delivery_id');
  if (originalId == null) {
    return null;
  }
  const workspaceId = requireWorkspaceId();
  const href = generatePath('/workspaces/:workspaceId/message_activity/sent/:deliveryId', {
    workspaceId,
    deliveryId: originalId,
  });
  return (
    <Box data-test-delivery-row-expansion-original-delivery>
      {/* `as="div"` so the id sits under its label, as in Ember — inline, the two would
          run together ("Original delivery<id>") since JSX drops the newline between them. */}
      <Text as="div" color="subtle" size="sm">
        Original delivery
      </Text>
      <Link href={href} variant="primary" isUnderlined={false}>
        {originalId}
      </Link>
    </Box>
  );
}
